import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  FileText,
  Image as ImageIcon,
  MapPin,
  Shapes,
  Users,
  Eye,
  Activity as ActivityIcon,
  ClipboardList,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { formatDate, formatDateTime } from '../lib/dates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DetailRow = ({ label, value }) => (
  <div className="grid gap-1 sm:grid-cols-[180px_1fr] sm:gap-4 py-2 border-b border-slate-100 last:border-b-0">
    <p className="text-sm font-medium text-slate-600">{label}</p>
    <div className="text-sm text-slate-800 break-words">{value || '—'}</div>
  </div>
);

const SectionCard = ({ icon: Icon, title, description, children }) => (
  <Card className="border border-[#D4C5A5]/30">
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-slate-800">
        <Icon className="w-5 h-5 text-[#8B1C4A]" />
        {title}
      </CardTitle>
      {description ? <CardDescription>{description}</CardDescription> : null}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

export const ActivityDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState(null);
  const [photos, setPhotos] = useState([]);

  const [dependencies, setDependencies] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [alcaldias, setAlcaldias] = useState([]);
  const [polygons, setPolygons] = useState([]);
  const [axes, setAxes] = useState([]);
  const [subaxes, setSubaxes] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  // const [targetPopulations, setTargetPopulations] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!token || !id) {
      setLoading(false);
      return;
    }

    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, token, id]);

  const fetchDetail = async () => {
    try {
      setLoading(true);

      const headers = { Authorization: `Bearer ${token}` };

      const results = await Promise.allSettled([
        axios.get(`${API}/activities/${id}`, { headers }),
        axios.get(`${API}/uploads/activity/${id}/photos`, { headers }),
        axios.get(`${API}/dependencies`, { headers }),
        axios.get(`${API}/territories`, { headers }),
        axios.get(`${API}/alcaldias`, { headers }),
        axios.get(`${API}/territory-polygons`, { headers }),
        axios.get(`${API}/axes`, { headers }),
        axios.get(`${API}/subaxes`, { headers }),
        axios.get(`${API}/activity-types`, { headers }),
        axios.get(`${API}/visit-types`, { headers }),
        // axios.get(`${API}/target-populations`, { headers }),
      ]);

      const activityRes = results[0];
      if (activityRes.status !== 'fulfilled') {
        throw activityRes.reason;
      }

      setActivity(activityRes.value?.data || null);
      setPhotos(results[1]?.status === 'fulfilled' ? results[1].value?.data || [] : []);
      setDependencies(results[2]?.status === 'fulfilled' ? results[2].value?.data || [] : []);
      setTerritories(results[3]?.status === 'fulfilled' ? results[3].value?.data || [] : []);
      setAlcaldias(results[4]?.status === 'fulfilled' ? results[4].value?.data || [] : []);
      setPolygons(results[5]?.status === 'fulfilled' ? results[5].value?.data || [] : []);
      setAxes(results[6]?.status === 'fulfilled' ? results[6].value?.data || [] : []);
      setSubaxes(results[7]?.status === 'fulfilled' ? results[7].value?.data || [] : []);
      setActivityTypes(results[8]?.status === 'fulfilled' ? results[8].value?.data || [] : []);
      setVisitTypes(results[9]?.status === 'fulfilled' ? results[9].value?.data || [] : []);
      // setTargetPopulations(results[10]?.status === 'fulfilled' ? results[10].value?.data || [] : []);
    } catch (error) {
      console.error('Error cargando detalle de actividad:', error);
      toast.error('No fue posible cargar el detalle de la actividad');
      setActivity(null);
    } finally {
      setLoading(false);
    }
  };

  const dependencyName = useMemo(() => {
    if (!activity) return '—';
    return (
      activity.dependency_name ||
      dependencies.find((item) => String(item.id) === String(activity.dependency_id))?.name ||
      '—'
    );
  }, [activity, dependencies]);

  const territoryName = useMemo(() => {
    if (!activity) return '—';
    return (
      activity.territory_name ||
      territories.find((item) => String(item.id) === String(activity.territory_id))?.name ||
      '—'
    );
  }, [activity, territories]);

  const alcaldiaName = useMemo(() => {
    if (!activity) return '—';
    return (
      activity.alcaldia_name ||
      alcaldias.find((item) => String(item.id) === String(activity.alcaldia_id))?.name ||
      '—'
    );
  }, [activity, alcaldias]);

  const polygonName = useMemo(() => {
    if (!activity) return '—';
    return (
      activity.territory_polygon_name ||
      polygons.find((item) => String(item.id) === String(activity.territory_polygon_id))?.name ||
      '—'
    );
  }, [activity, polygons]);

  const axisName = useMemo(() => {
    if (!activity) return '—';
    return (
      activity.axis_name ||
      axes.find((item) => String(item.id) === String(activity.axis_id))?.name ||
      '—'
    );
  }, [activity, axes]);

  const subaxisNames = useMemo(() => {
    if (!activity?.subaxis_ids?.length) return [];
    return activity.subaxis_ids
      .map((idItem) => subaxes.find((item) => String(item.id) === String(idItem))?.name)
      .filter(Boolean);
  }, [activity, subaxes]);

  const activityTypeName = useMemo(() => {
    if (!activity) return '—';
    return (
      activity.activity_type_name ||
      activityTypes.find((item) => String(item.id) === String(activity.activity_type_id))?.name ||
      '—'
    );
  }, [activity, activityTypes]);

  const visitTypeName = useMemo(() => {
    if (!activity?.visit_type_id) return '—';
    return (
      activity.visit_type_name ||
      visitTypes.find((item) => String(item.id) === String(activity.visit_type_id))?.name ||
      '—'
    );
  }, [activity, visitTypes]);

  const participantDependencyNames = useMemo(() => {
    if (!activity?.participant_dependency_ids?.length) return [];
    return activity.participant_dependency_ids
      .map((idItem) => dependencies.find((item) => String(item.id) === String(idItem))?.name)
      .filter(Boolean);
  }, [activity, dependencies]);

  const showProgressPercentage = useMemo(() => {
    const name = (activityTypeName || '').toUpperCase();
    return name.includes('OBRA') || name.includes('PROYECTO');
  }, [activityTypeName]);

  /* const targetPopulationNames = useMemo(() => {
    if (!activity?.target_population_ids?.length) return [];
    return activity.target_population_ids
      .map((idItem) => targetPopulations.find((item) => String(item.id) === String(idItem))?.name)
      .filter(Boolean);
  }, [activity, targetPopulations]); */

  const target_population_names = activity?.target_populations ?
    activity.target_populations.map((item) => item.name).join(', ') :
    '—'

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F0E1] py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border border-[#D4C5A5]/30">
            <CardContent className="py-10 text-center text-slate-500">
              Cargando detalle de actividad...
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="min-h-screen bg-[#F5F0E1] py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border border-[#D4C5A5]/30">
            <CardContent className="py-10 text-center">
              <p className="text-slate-600">No se encontró la actividad.</p>
              <Button className="mt-4" variant="outline" onClick={() => navigate(-1)}>
                Volver
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const title = activity.title || 'Actividad sin título';
  const statusLabel = activity.status === 'published' ? 'Publicada' : activity.status || '—';

  return (
    <div className="min-h-screen bg-[#F5F0E1] py-8" data-testid="activity-detail-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-3">
            <Button variant="outline" onClick={() => navigate(-1)} className="w-fit">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>

            <div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge className="bg-green-100 text-green-800">{statusLabel}</Badge>
                <Badge variant="outline">ID: {activity.id}</Badge>
              </div>
              <h1 className="text-3xl font-bold text-slate-800">{title}</h1>
              <p className="text-slate-600 mt-2">
                Detalle completo de la actividad registrada.
              </p>
            </div>
          </div>

        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card className="border border-[#D4C5A5]/30">
            <CardContent className="py-5">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-[#8B1C4A]" />
                <div>
                  <p className="text-xs text-slate-500">Beneficiarios</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {activity.total_beneficiaries ?? 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[#D4C5A5]/30">
            <CardContent className="py-5">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-[#BC955C]" />
                <div>
                  <p className="text-xs text-slate-500">Asistentes</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {activity.total_attendees ?? 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[#D4C5A5]/30">
            <CardContent className="py-5">
              <div className="flex items-center gap-3">
                <ActivityIcon className="w-5 h-5 text-[#235B4E]" />
                <div>
                  <p className="text-xs text-slate-500">Visitas</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {activity.total_visits ?? 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[#D4C5A5]/30">
            <CardContent className="py-5">
              <div className="flex items-center gap-3">
                <CalendarDays className="w-5 h-5 text-slate-700" />
                <div>
                  <p className="text-xs text-slate-500">Periodo</p>
                  <p className="text-sm font-semibold text-slate-800">
                    {formatDate(activity.start_date) || '—'}{' '}
                    {activity.end_date ? `al ${formatDate(activity.end_date)}` : ''}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <SectionCard
          icon={ClipboardList}
          title="Datos generales"
          description="Información principal de la actividad."
        >
          <DetailRow label="Título" value={activity.title} />
          <DetailRow label="Descripción" value={activity.description} />
          <DetailRow label="Detalle de actividad" value={activity.detalle_actividad} />
          <DetailRow label="Fecha de inicio" value={formatDate(activity.start_date)} />
          <DetailRow label="Fecha de cierre" value={formatDate(activity.end_date)} />
          <DetailRow label="Estado" value={statusLabel} />
          <DetailRow label="Creado por" value={activity.responsible_name || activity.created_by} />
          <DetailRow label="Creada el" value={formatDateTime(activity.created_at)} />
          <DetailRow label="Actualizada el" value={formatDateTime(activity.updated_at)} />
        </SectionCard>

        <SectionCard
          icon={MapPin}
          title="Ubicación territorial"
          description="Datos de alcaldía, territorio y polígono."
        >
          <DetailRow label="Alcaldía" value={alcaldiaName} />
          <DetailRow label="Territorio" value={territoryName} />
          <DetailRow label="Polígono" value={polygonName} />
          <DetailRow label="Calle o referencia" value={activity.street_reference} />
        </SectionCard>

        <SectionCard
          icon={Building2}
          title="Información institucional"
          description="Dependencias y clasificación operativa."
        >
          <DetailRow label="Dependencia principal" value={dependencyName} />
          <DetailRow label="Eje" value={axisName} />
          <DetailRow label="Subejes / líneas de acción" value={subaxisNames.join(', ')} />
          <DetailRow label="Tipo de actividad" value={activityTypeName} />
          <DetailRow label="Tipo de visita" value={visitTypeName} />
          <DetailRow
            label="Actividad conjunta"
            value={activity.is_joint_activity ? 'Sí' : 'No'}
          />
          <DetailRow
            label="Dependencias participantes"
            value={participantDependencyNames.join(', ')}
          />
        </SectionCard>

<SectionCard
  icon={Users}
  title="Población y métricas"
  description="Población objetivo y resultados cuantitativos."
>
  <DetailRow
    label="Población objetivo"
    // value={targetPopulationNames.join(', ')}
    value={target_population_names}
  />
  <DetailRow label="Total de visitas" value={String(activity.total_visits ?? 0)} />
  <DetailRow label="Total de asistentes" value={String(activity.total_attendees ?? 0)} />
  <DetailRow
    label="Total de beneficiarios"
    value={String(activity.total_beneficiaries ?? 0)}
  />

  {showProgressPercentage && (
    <DetailRow
      label="Porcentaje de avance"
      value={`${activity.progress_percentage ?? 0}%`}
    />
  )}
</SectionCard>

        <SectionCard
          icon={FileText}
          title="Resultados y observaciones"
          description="Narrativa cualitativa de la actividad."
        >
          <DetailRow label="Resultados" value={activity.results} />
          <DetailRow label="Observaciones" value={activity.observations} />
        </SectionCard>

        <SectionCard
          icon={Shapes}
          title="Evidencia fotográfica"
          description="Fotos asociadas a la actividad desde uploads."
        >
          {photos.length === 0 ? (
            <p className="text-sm text-slate-500">No hay fotos registradas para esta actividad.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {photos.map((photo) => {
                const src =
                  photo.photo_url?.startsWith('http')
                    ? photo.photo_url
                    : `${process.env.REACT_APP_BACKEND_URL}${photo.photo_url}`;

                return (
                  <div
                    key={photo.id}
                    className="overflow-hidden rounded-lg border border-slate-200 bg-white"
                  >
                    <div className="aspect-[4/3] bg-slate-100">
                      <img
                        src={src}
                        alt={`Foto actividad ${photo.position || ''}`}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="p-3 text-sm text-slate-600">
                      <p>
                        <span className="font-medium">Posición:</span>{' '}
                        {photo.position || '—'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
};

export default ActivityDetail;